import React from 'react';
import { Link } from 'react-router-dom';
import { appPath } from '../../../utils/appPath';

/* ─── Types ──────────────────────────────────────────────────────────────── */

export interface CurrencyRow {
  ccy: string;
  clientCount: number;
  invoicedBase: number;
  invoicedUSD: number;
}

export interface KpiStripProps {
  totalClients: number;         // kept for API compat — not displayed
  totalInvoicedUSD: number;
  totalOutstandingUSD: number;
  totalDeposit: number;
  totalDepositAdj: number;
  totalBalance: number;
  currencyRows: CurrencyRow[];
}

/* ─── Formatters ─────────────────────────────────────────────────────────── */

const fmtUSD = (n: number): string => {
  const abs = Math.abs(n);
  const sign = n < 0 ? '−$' : '$';
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000)     return `${sign}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const fmtBase = (n: number): string =>
  n.toLocaleString('en-US', { maximumFractionDigits: 0 });

/* ─── Card 1: Revenue Overview ───────────────────────────────────────────── */

export interface RevenueOverviewCardProps {
  invoiced: number;
  outstanding: number;
  /** Optional sub-line under total invoiced (e.g. base-currency amount) */
  invoicedDetail?: string;
  /** Optional sub-line under outstanding (e.g. base-currency amount) */
  outstandingDetail?: string;
  /** Card title — default matches Client Management list KPI */
  title?: string;
}

export const RevenueOverviewCard: React.FC<RevenueOverviewCardProps> = ({
  invoiced,
  outstanding,
  invoicedDetail,
  outstandingDetail,
  title = 'Revenue Overview',
}) => {
  const outPct = invoiced > 0 ? (outstanding / invoiced) * 100 : 0;
  const collectedPct = Math.max(0, 100 - outPct);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md flex items-stretch gap-0 overflow-hidden flex-1 min-w-0">
      <div className="w-1 flex-none bg-blue-500" />

      <div className="flex-1 min-w-0 p-4">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
          {title}
        </p>

        <div className="mb-3">
          <p className="text-[11px] text-slate-500 font-medium mb-0.5">Total Invoiced</p>
          <p className="text-2xl font-bold text-[#0D0D0D] tabular-nums leading-none">
            {fmtUSD(invoiced)}
          </p>
          {invoicedDetail && (
            <p className="text-[11px] text-slate-600 tabular-nums mt-1">{invoicedDetail}</p>
          )}
        </div>

        <div className="mb-3">
          <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-blue-500 transition-all duration-500"
              style={{ width: `${collectedPct.toFixed(1)}%` }}
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-slate-600">{collectedPct.toFixed(0)}% collected</span>
            <span className="text-[10px] text-slate-600">{outPct.toFixed(0)}% open</span>
          </div>
        </div>

        <div className="border-t border-slate-100 pt-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] text-slate-500 font-medium mb-0.5">Outstanding (AR)</p>
            <p className="text-base font-bold tabular-nums leading-none text-amber-600">
              {fmtUSD(outstanding)}
            </p>
            {outstandingDetail && (
              <p className="text-[11px] text-slate-600 tabular-nums mt-1">{outstandingDetail}</p>
            )}
          </div>
          {outPct > 0 && (
            <span
              className={`
              shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full tabular-nums
              ${outPct > 25 ? 'bg-red-50 text-red-600' : outPct > 10 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}
            `}
            >
              {outPct.toFixed(1)}% of inv.
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Card 2: Deposit Position ───────────────────────────────────────────── */

export interface DepositPositionCardProps {
  deposit: number;
  adjustment: number;
  balance: number;
  depositDetail?: string;
  adjustmentDetail?: string;
  balanceDetail?: string;
  title?: string;
}

export const DepositPositionCard: React.FC<DepositPositionCardProps> = ({
  deposit,
  adjustment,
  balance,
  depositDetail,
  adjustmentDetail,
  balanceDetail,
  title = 'Deposit Position',
}) => {
  const balancePositive = balance >= 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md flex items-stretch gap-0 overflow-hidden flex-1 min-w-0">
      <div className={`w-1 flex-none ${balancePositive ? 'bg-emerald-500' : 'bg-red-500'}`} />

      <div className="flex-1 min-w-0 p-4">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
          {title}
        </p>

        <div className="mb-3">
          <p className="text-[11px] text-slate-500 font-medium mb-0.5">Net Balance</p>
          <p
            className={`text-2xl font-bold tabular-nums leading-none ${balancePositive ? 'text-emerald-700' : 'text-red-600'}`}
          >
            {fmtUSD(balance)}
          </p>
          {balanceDetail && (
            <p className="text-[11px] text-slate-600 tabular-nums mt-1">{balanceDetail}</p>
          )}
        </div>

        <div className="mb-3">
          <span
            className={`
            inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full
            ${balancePositive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}
          `}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${balancePositive ? 'bg-emerald-500' : 'bg-red-500'}`}
            />
            {balancePositive ? 'Positive balance' : 'Deficit balance'}
          </span>
        </div>

        <div className="border-t border-slate-100 pt-3 grid grid-cols-2 gap-3">
          <div className="min-w-0">
            <p className="text-[10px] text-slate-500 font-medium mb-0.5">Deposit Total</p>
            <p className="text-sm font-bold text-slate-700 tabular-nums leading-none">
              {fmtUSD(deposit)}
            </p>
            {depositDetail && (
              <p className="text-[11px] text-slate-600 tabular-nums mt-1 truncate" title={depositDetail}>
                {depositDetail}
              </p>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-slate-500 font-medium mb-0.5">Adjustment</p>
            <p className="text-sm font-bold text-slate-700 tabular-nums leading-none">
              {fmtUSD(adjustment)}
            </p>
            {adjustmentDetail && (
              <p
                className="text-[11px] text-slate-600 tabular-nums mt-1 truncate"
                title={adjustmentDetail}
              >
                {adjustmentDetail}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Currency Table ─────────────────────────────────────────────────────── */

const CurrencyTable: React.FC<{ rows: CurrencyRow[] }> = ({ rows }) => {
  if (rows.length === 0) return null;

  const totalUSD = rows.reduce((s, r) => s + r.invoicedUSD, 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md flex flex-col overflow-hidden min-w-0 flex-[1.6]">
      {/* Header */}
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 rounded-full bg-violet-500" />
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">
            Currency Breakdown
          </span>
        </div>
        <Link
          to={appPath('/currency-dashboard')}
          className="text-[11px] font-medium text-violet-600 hover:text-violet-800 transition-colors"
        >
          Currency Dashboard →
        </Link>
      </div>

      {/* Table */}
      <div className="overflow-auto flex-1">
        <table className="w-full text-[12px] border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              <th className="text-left px-4 py-2 font-semibold text-slate-500 whitespace-nowrap">Currency</th>
              <th className="text-right px-4 py-2 font-semibold text-slate-500 whitespace-nowrap">Clients</th>
              <th className="text-right px-4 py-2 font-semibold text-slate-500 whitespace-nowrap">Invoiced (base)</th>
              <th className="text-right px-4 py-2 font-semibold text-slate-500 whitespace-nowrap">Invoiced (USD)</th>
              <th className="text-right px-4 py-2 font-semibold text-slate-500 whitespace-nowrap">Share</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {rows.map(row => {
              const share = totalUSD > 0 ? (row.invoicedUSD / totalUSD) * 100 : 0;
              return (
                <tr key={row.ccy} className="hover:bg-slate-50/80 transition-colors">
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                        {row.ccy}
                      </span>
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums font-medium">
                    {row.clientCount}
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-700 tabular-nums font-medium">
                    {fmtBase(row.invoicedBase)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-900 tabular-nums font-semibold">
                    {fmtUSD(row.invoicedUSD)}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-violet-400"
                          style={{ width: `${share.toFixed(1)}%` }}
                        />
                      </div>
                      <span className="text-[11px] text-slate-500 tabular-nums w-9 text-right">
                        {share.toFixed(0)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 1 && (
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50/50">
                <td className="px-4 py-2 text-[11px] font-semibold text-slate-500">Total</td>
                <td className="px-4 py-2 text-right text-[11px] font-semibold text-slate-600 tabular-nums">
                  {rows.reduce((s, r) => s + r.clientCount, 0)}
                </td>
                <td className="px-4 py-2 text-right text-[11px] text-slate-600">—</td>
                <td className="px-4 py-2 text-right text-[11px] font-bold text-slate-800 tabular-nums">
                  {fmtUSD(totalUSD)}
                </td>
                <td className="px-4 py-2 text-right text-[11px] font-semibold text-slate-500">100%</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

/* ─── Main KpiStrip ──────────────────────────────────────────────────────── */

export const KpiStrip: React.FC<KpiStripProps> = ({
  totalInvoicedUSD,
  totalOutstandingUSD,
  totalDeposit,
  totalDepositAdj,
  totalBalance,
  currencyRows,
}) => (
  <div className="flex gap-3 flex-wrap lg:flex-nowrap items-stretch">
    <RevenueOverviewCard invoiced={totalInvoicedUSD} outstanding={totalOutstandingUSD} />
    <DepositPositionCard deposit={totalDeposit} adjustment={totalDepositAdj} balance={totalBalance} />
    <CurrencyTable rows={currencyRows} />
  </div>
);

export default KpiStrip;
